import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { AbstractDynamicComponent } from '@components/abstracts';
import { HeroSlider } from '@components/hero-slider/models';
import { VirtualTourDialogComponent } from '@components/virtual-tour-dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonMiddleOfficeComponent } from '@shared/components/button-middle-office';
import { ImageComponent } from '@shared/components/image';
import { Image } from '@shared/components/image/models';
import { getCDNLink } from '@shared/components/image/utils';
import { SvgComponent } from '@shared/components/svg';
import { DialogService } from '@shared/services/dialog/dialog.service';
import { SeoService } from '@shared/services/seo';
import { PromotionStore } from '@stores';

@Component({
  selector: 'app-hero-slider',
  imports: [ImageComponent, SvgComponent, NgClass, TranslatePipe, ButtonMiddleOfficeComponent],
  templateUrl: './hero-slider.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroSliderComponent extends AbstractDynamicComponent<HeroSlider> implements OnInit {
  readonly slidesElementRef = viewChild.required<ElementRef<HTMLElement>>('slidesElementRef');
  sliderImages = computed(() => this.computeSliderImages());
  sliderImagesCount = computed(() => this.sliderImages().length);
  currentSliderIndex = signal(0);
  private readonly dialogService = inject(DialogService);
  private readonly seoService = inject(SeoService);
  private readonly heroSliderPromo = inject(PromotionStore).select('heroSliderPromo');

  ngOnInit() {
    this.injectStoreJsonLd();
  }

  moveSlider(direction: 'prev' | 'next'): void {
    const nImages = this.sliderImagesCount();

    if (nImages <= 1) return;

    const slideWidth = this.slidesElementRef().nativeElement.clientWidth;
    let newIndex = this.currentSliderIndex();

    if (direction === 'next') {
      newIndex = (newIndex + 1) % nImages;
    } else {
      newIndex = (newIndex - 1 + nImages) % nImages;
    }

    this.currentSliderIndex.set(newIndex);
    this.slidesElementRef().nativeElement.scrollTo({
      left: newIndex * slideWidth,
      behavior: 'smooth',
    });
  }

  openVirtualTour(url: string): void {
    if (!url) return;

    this.dialogService.openDialog(VirtualTourDialogComponent, 'FULL_SCREEN', { data: { url } });
  }

  private injectStoreJsonLd() {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ImageObject',
      contentUrl: getCDNLink(this.data.store),
      description: this.data.store.alt,
      creator: { '@type': 'Organization', name: 'Fournier Groupe' },
      creditText: 'Fournier Groupe',
      copyrightNotice: '© Fournier Groupe',
    };
    this.seoService.addJsonLdScript('hero-slider-schema', schema);
  }

  private computeSliderImages() {
    let sliderImages: ImageWithUrl[] = [];
    const promo = this.heroSliderPromo();
    const isPromoVisible = !this.data.isPromoHidden;
    if (promo && isPromoVisible) sliderImages.push(promo);
    sliderImages = [...sliderImages, ...this.data.slider];

    if (this.data.virtualTour && (this.data.virtualTour.url || sliderImages.length < 1)) {
      sliderImages.push({
        ...this.data.virtualTour.preview,
        url: this.data.virtualTour.url,
      });
    }

    return sliderImages;
  }
}

interface ImageWithUrl extends Image {
  url?: string;
}
